import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Fade,
  Grid,
  IconButton,
  Paper,
  Typography,
} from "@mui/material";
import InvitationManagement from "./InvitationManagement";
import { useTranslation } from "react-i18next";
import { useLoginStatus } from "../../../context/LoginStatusContext";
import EditIcon from "@mui/icons-material/Edit";
import TextField from "@mui/material/TextField";
import axios from "axios";
import Cookies from "js-cookie";
import ValidationTextField from "../../../components/common/ValidationTextField";
import AvatarUpload from "./AvatarUpload";
import { useAlert } from "../../../context/AlertContext";
import DeleteIcon from "@mui/icons-material/Delete";
import CancelIcon from "@mui/icons-material/Cancel";
import LogoutIcon from "@mui/icons-material/Logout";
import SaveIcon from "@mui/icons-material/Save";
import { CustomSection } from "../../../components/common/CustomSection";
import { useNotice } from "../../../context/NoticeContext";
import { alpha } from "@mui/material/styles";
import { AccountBox } from "@mui/icons-material";

const Account = () => {
  const { t } = useTranslation();
  const { user } = useLoginStatus();
  const [isEditingUserName, setIsEditingUserName] = useState(false);
  const [isEditingIntroduction, setIsEditingIntroduction] = useState(false);
  const [username, setUsername] = useState(user?.username);
  const [introduction, setIntroduction] = useState("");
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const { setAlert } = useAlert();
  const { triggerNotice } = useNotice();

  const handleEditUserName = () => {
    setIsEditingUserName(true);
  };

  const handleEditIntroduction = () => {
    setIsEditingIntroduction(true);
  };

  const getUserIntroduction = async () => {
    try {
      const res = await axios.get("/api/v1/users/user");
      setIntroduction(res.data.introduction || "");
    } catch (e) {
      console.log(e);
    }
  };

  // userが変更されたときにローカル状態を同期
  useEffect(() => {
    if (user) {
      setUsername(user.username);
      getUserIntroduction();
    }
  }, [user]);

  const handleIntroductionSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    setIsLoadingIntroduction(true);
    try {
      const url = "/api/v1/users/update-introduction";
      axios.defaults.withCredentials = true;

      // CSRFトークンの取得
      const csrftoken = Cookies.get("csrftoken");
      // リクエストヘッダーにCSRFトークンを追加
      const config = {
        headers: { "X-CSRFToken": csrftoken },
      };

      const response = await axios.patch(url, { introduction }, config);

      if (response.data.success) {
        console.log("Introduction updated successfully.");
        setIsEditingIntroduction(false);
        setIntroductionErrorMessage("");
      } else {
        console.log("Error updating introduction:", response.data.message);
      }
    } catch (error: unknown) {
      const apiError = error as ApiError;
      console.log("An error occurred while updating introduction:", apiError.response?.data);
      if (apiError.response?.data?.message && typeof apiError.response.data.message === "object") {
        const message = apiError.response.data.message as Record<string, string[]>;
        setIntroductionErrorMessage(message.introduction?.[0] || "エラーが発生しました");
      }
    }
    setIsLoadingIntroduction(false);
  };

  const handleIntroductionCancel = () => {
    getUserIntroduction();
    setIntroductionErrorMessage("");
    setIsEditingIntroduction(false);
  };

  const [isLoadingUsername, setIsLoadingUsername] = useState(false);
  const [isLoadingIntroduction, setIsLoadingIntroduction] = useState(false);
  const [usernameErrorMessage, setUsernameErrorMessage] = useState("");
  const [introductionErrorMessage, setIntroductionErrorMessage] = useState("");

  const handleUsernameSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    setIsLoadingUsername(true);
    try {
      const url = "/api/v1/users/update-username";
      axios.defaults.withCredentials = true;

      // CSRFトークンの取得
      const csrftoken = Cookies.get("csrftoken");
      // リクエストヘッダーにCSRFトークンを追加
      const config = {
        headers: { "X-CSRFToken": csrftoken },
      };

      const response = await axios.patch(url, { username }, config);

      if (response.data.success) {
        console.log("Username updated successfully.");
        setIsEditingUserName(false);
        setUsernameErrorMessage("");
      } else {
        console.log("Error updating username:", response.data.message);
      }
    } catch (error: unknown) {
      const apiError = error as ApiError;
      console.log("An error occurred while updating username:", apiError.response?.data);
      if (apiError.response?.data?.message && typeof apiError.response.data.message === "object") {
        const message = apiError.response.data.message as Record<string, string[]>;
        setUsernameErrorMessage(message.username?.[0] || "ユーザー名の更新中にエラーが発生しました");
      }
    }
    setIsLoadingUsername(false);
  };

  const handleUserNameCancel = () => {
    setUsername(user?.username);
    setUsernameErrorMessage("");
    setIsEditingUserName(false);
  };

  const loginStatus = useLoginStatus();

  // 組織情報のインターフェース
  interface OrganizationInfo {
    id: string;
    name: string;
    role: string;
    user_id?: number;
  }

  // API エラーの型定義
  type ApiError = {
    response?: {
      data?: {
        message?: Record<string, string[]> | string;
        error?: string;
        organization_name?: string;
      };
      status?: number;
    };
    message?: string;
  };

  // 組織選択用の状態
  const [organizationDialogOpen, setOrganizationDialogOpen] = useState(false);
  const [availableOrganizations, setAvailableOrganizations] = useState<OrganizationInfo[]>([]);
  const [selectedOrganization, setSelectedOrganization] = useState<string | null>(null);
  const [loadingOrganizations, setLoadingOrganizations] = useState(false);

  // 利用可能な組織を取得
  const fetchAvailableOrganizations = async () => {
    setLoadingOrganizations(true);
    try {
      const csrftoken = Cookies.get("csrftoken");
      const response = await axios.get("/api/v1/enterprise/users/me/switch-mode/", {
        headers: { "X-CSRFToken": csrftoken },
      });

      if (response.data.organizations && response.data.organizations.length > 0) {
        setAvailableOrganizations(response.data.organizations);
        // デフォルトで最初の組織を選択
        setSelectedOrganization(response.data.organizations[0].id);
      } else {
        setAlert("info", "利用可能な組織がありません");
      }
    } catch (error) {
      console.error("組織情報取得エラー:", error);
      setAlert("error", "組織情報の取得に失敗しました");
    } finally {
      setLoadingOrganizations(false);
    }
  };

  // 個人モードへの切り替え
  const switchToPersonalMode = async () => {
    try {
      const csrftoken = Cookies.get("csrftoken");
      const config = {
        headers: { "X-CSRFToken": csrftoken },
      };

      const response = await axios.post("/api/v1/enterprise/users/me/switch-mode/", { mode: "personal" }, config);

      if (response.data.success) {
        // エンタープライズユーザーから個人ユーザーへの切り替えの場合

        // 通常の更新（個人ユーザーのまま）
        loginStatus.refreshUser();
        triggerNotice("success", t("preferences.account.switchedToPersonal"));
      } else {
        setAlert("error", response.data.message || "モードの切り替えに失敗しました");
      }
    } catch (error: unknown) {
      const apiError = error as ApiError;
      console.error("モード切替エラー:", apiError);
      setAlert("error", apiError.response?.data?.error || "個人モードへの切り替えに失敗しました");
    }
  };

  // エンタープライズモードへの切り替え（組織IDを指定）
  const switchToEnterpriseMode = async (organizationUuid: string) => {
    try {
      const csrftoken = Cookies.get("csrftoken");
      const config = {
        headers: { "X-CSRFToken": csrftoken },
      };

      const response = await axios.post(
        "/api/v1/enterprise/users/me/switch-mode/",
        { mode: "enterprise", organization_uuid: organizationUuid },
        config
      );

      if (response.data.success) {
        // ユーザー情報を更新
        loginStatus.refreshUser();
        triggerNotice("success", t("preferences.account.switchedToEnterprise"));
        setOrganizationDialogOpen(false);
      } else {
        setAlert("error", response.data.message);
      }
    } catch (error: unknown) {
      const apiError = error as ApiError;
      console.error("モード切替エラー:", apiError);
      setAlert("error", apiError.response?.data?.error || "エンタープライズモードへの切り替えに失敗しました");
    }
  };

  // アカウントタイプ切り替え処理
  const handleSwitchMode = async () => {
    if (user?.user_type === "personal") {
      // 個人→エンタープライズの場合、組織選択ダイアログを表示
      await fetchAvailableOrganizations();
      setOrganizationDialogOpen(true);
    } else {
      // エンタープライズ→個人の場合、直接切り替え
      await switchToPersonalMode();
    }
  };

  // 組織選択ダイアログの確認処理
  const handleOrganizationDialogConfirm = () => {
    if (selectedOrganization) {
      switchToEnterpriseMode(selectedOrganization);
    } else {
      setAlert("error", "組織を選択してください");
    }
  };

  const handleLogout = async () => {
    try {
      await axios.get("/api/v1/auth/logout");
      loginStatus.checkLoginStatus();
      triggerNotice("info", `You have successfully logged out.`);
      // navigate('');
    } catch (error) {
      console.error("An unknown error occurred:", error);
    }
  };

  const handleDelete = async () => {
    try {
      const csrftoken = Cookies.get("csrftoken");
      const res = await axios.delete("/api/v1/users/user", {
        headers: { "X-CSRFToken": csrftoken },
      });
      if (res.data.success) {
        loginStatus.checkLoginStatus();
        triggerNotice("info", res.data.message || t("account.deleteDialog.deactivated"));
      } else {
        setAlert("error", res.data.message);
      }
    } catch (error: unknown) {
      const apiError = error as ApiError;
      console.error("An error occurred:", apiError);

      // オーナー保護エラーの特別な処理
      if (apiError.response?.data?.error === "owner_protection") {
        const organizationName = apiError.response?.data?.organization_name || "";
        setAlert("error", t("preferences.account.deleteDialog.error.owner_protection_with_org", { organizationName }));
      } else {
        const errorMessage =
          typeof apiError.response?.data?.message === "string"
            ? apiError.response.data.message
            : t("preferences.account.deleteDialog.error.common");
        setAlert("error", errorMessage);
      }
    }
  };

  return (
    <>
      <Box component="div" pb={8}>
        <Typography component={"h3"} variant="h5" gutterBottom my={2}>
          {t("preferences.account.title")}
        </Typography>

        <Fade in={true} timeout={500}>
          <Paper
            elevation={0}
            sx={{
              width: "100%",
              backgroundColor: (theme) => alpha(theme.palette.background.paper, 0.7),
              backdropFilter: "blur(10px)",
              border: (theme) => `1px solid ${alpha(theme.palette.grey[500], 0.2)}`,
              borderRightColor: (theme) => alpha(theme.palette.grey[900], 0.5),
              borderBottomColor: (theme) => alpha(theme.palette.grey[900], 0.5),
              mb: 2,
            }}
          >
            {/* アカウントタイプ切り替え：組織に所属している場合のみ表示 */}
            {user?.has_enterprise_memberships && (
              <CustomSection>
                <Grid container sx={{ justifyContent: "space-between", alignItems: "center" }}>
                  <Grid
                    size={{
                      xs: 12,
                      md: 6,
                    }}
                  >
                    <Typography variant="subtitle1">{t("preferences.account.modeSwitch")}</Typography>
                    <Typography variant="body2">{t("preferences.account.modeSwitchDescription")}</Typography>
                  </Grid>
                  <Grid
                    sx={{ display: "flex", justifyContent: "flex-end" }}
                    size={{
                      xs: 12,
                      md: 6,
                    }}
                  >
                    <Button
                      disableElevation
                      onClick={handleSwitchMode}
                      variant="contained"
                      color="primary"
                      sx={{ width: 250 }}
                      startIcon={<AccountBox />}
                    >
                      {user.user_type === "personal"
                        ? t("preferences.account.switchToEnterprise")
                        : t("preferences.account.switchToPersonal")}
                    </Button>
                  </Grid>
                </Grid>
              </CustomSection>
            )}

            {/* 組織招待管理セクション */}
            <CustomSection>
              <Grid container sx={{ justifyContent: "space-between", alignItems: "flex-start" }}>
                <Grid
                  size={{
                    xs: 12,
                    md: 6,
                  }}
                >
                  <Typography variant="subtitle1">{t("preferences.invitation.title")}</Typography>
                  <Typography variant="body2">{t("preferences.invitation.description")}</Typography>
                </Grid>
                <Grid
                  sx={{ width: "100%" }}
                  size={{
                    xs: 12,
                    md: 6,
                  }}
                >
                  <Box sx={{ width: "100%" }}>
                    <InvitationManagement />
                  </Box>
                </Grid>
              </Grid>
            </CustomSection>
          </Paper>
        </Fade>

        <Fade in={true} timeout={500}>
          <Paper
            elevation={0}
            sx={{
              width: "100%",
              backgroundColor: (theme) => alpha(theme.palette.background.paper, 0.7),
              backdropFilter: "blur(10px)",
              border: (theme) => `1px solid ${alpha(theme.palette.grey[500], 0.2)}`,
              borderRightColor: (theme) => alpha(theme.palette.grey[900], 0.5),
              borderBottomColor: (theme) => alpha(theme.palette.grey[900], 0.5),
            }}
          >
            <CustomSection>
              <Grid container sx={{ justifyContent: "space-between", alignItems: "center" }}>
                <Grid
                  size={{
                    xs: 12,
                    md: 6,
                  }}
                >
                  <Typography variant="subtitle1">{t("preferences.account.username")}</Typography>
                </Grid>
                <Grid
                  sx={{ display: "flex", justifyContent: "flex-end" }}
                  size={{
                    xs: 12,
                    md: 6,
                  }}
                >
                  {isEditingUserName ? (
                    <form onSubmit={handleUsernameSubmit} style={{ width: "100%" }}>
                      <Box display={"flex"} flexDirection={"column"} alignItems={"baseline"} gap={1} width={"100%"}>
                        <ValidationTextField
                          label="Username"
                          type="username"
                          value={username ? username : ""}
                          variant={"standard"}
                          helperText={usernameErrorMessage}
                          error={usernameErrorMessage !== ""}
                          onChange={(e) => setUsername(e.target.value)}
                          autoFocus={true}
                          toolTipPlacement={"top"}
                          tooltipMessage={t("top.tooltip.username")}
                          fullWidth
                          inputProps={{ style: { textTransform: "lowercase" } }}
                        />
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "row",
                            alignItems: "center",
                            gap: 1,
                            width: "100%",
                          }}
                        >
                          <Box sx={{ flexGrow: 1 }} />
                          <Button
                            disableElevation
                            onClick={handleUserNameCancel}
                            variant="text"
                            color="primary"
                            size="small"
                            sx={{ minWidth: "75px" }}
                          >
                            {t("common.cancel")}
                          </Button>
                          <Button
                            disableElevation
                            type={"submit"}
                            variant="outlined"
                            color="primary"
                            startIcon={<SaveIcon />}
                            size="small"
                            disabled={isLoadingUsername}
                          >
                            {t("common.save")}
                          </Button>
                        </Box>
                      </Box>
                    </form>
                  ) : (
                    <Box flexDirection={"row"} sx={{ display: "flex", alignItems: "flex-end", width: "100%" }}>
                      <TextField variant={"standard"} disabled value={username} sx={{ width: "100%", marginLeft: 1 }} />
                      <IconButton onClick={handleEditUserName} size="small">
                        <EditIcon fontSize="inherit" />
                      </IconButton>
                    </Box>
                  )}
                </Grid>
              </Grid>
            </CustomSection>

            {/* メールアドレス */}
            <CustomSection>
              <Grid container sx={{ justifyContent: "space-between", alignItems: "center" }}>
                <Grid
                  size={{
                    xs: 12,
                    md: 6,
                  }}
                >
                  <Typography variant="subtitle1">{t("preferences.account.email")}</Typography>
                </Grid>
                <Grid
                  sx={{ display: "flex", justifyContent: "flex-end" }}
                  size={{
                    xs: 12,
                    md: 6,
                  }}
                >
                  <Typography variant="body1" mr={1}>
                    {user?.email}
                  </Typography>
                </Grid>
              </Grid>
            </CustomSection>

            {/* アバター */}
            <CustomSection>
              <Grid container sx={{ justifyContent: "space-between", alignItems: "center" }}>
                <Grid
                  size={{
                    xs: 12,
                    md: 6,
                  }}
                >
                  <Typography variant="subtitle1">{t("preferences.account.avatar")}</Typography>
                </Grid>
                <Grid
                  sx={{ display: "flex", justifyContent: "flex-end" }}
                  size={{
                    xs: 12,
                    md: 6,
                  }}
                >
                  <AvatarUpload isAvatar={true} user={user} editable={true} />
                </Grid>
              </Grid>
            </CustomSection>

            {/* プロフィール文 */}
            <CustomSection>
              <Grid container sx={{ justifyContent: "space-between", alignItems: "center" }}>
                <Grid
                  size={{
                    xs: 12,
                    md: 6,
                  }}
                >
                  <Typography variant="subtitle1">{t("preferences.account.introduction")}</Typography>
                  <Typography variant="body2">{t("preferences.account.introductionDescription")}</Typography>
                </Grid>
                <Grid
                  sx={{ display: "flex", justifyContent: "flex-end" }}
                  size={{
                    xs: 12,
                    md: 6,
                  }}
                >
                  {isEditingIntroduction ? (
                    <form onSubmit={handleIntroductionSubmit} style={{ width: "100%" }}>
                      <Box display={"flex"} flexDirection={"column"} alignItems={"baseline"} gap={1} width={"100%"}>
                        <Box width={"100%"}>
                          <ValidationTextField
                            label="Introduction"
                            type="introduction"
                            value={introduction ? introduction : ""}
                            variant={"standard"}
                            helperText={introductionErrorMessage}
                            error={introductionErrorMessage !== ""}
                            onChange={(e) => setIntroduction(e.target.value)}
                            placeholder={t("preferences.account.introductionPlaceholder")}
                            autoFocus={true}
                            fullWidth
                            multiline
                            rows={5}
                            inputProps={{
                              maxLength: 500,
                            }}
                          />
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{
                              display: "flex",
                              justifyContent: "flex-end",
                              width: "100%",
                            }}
                          >
                            {introduction.length}/500
                          </Typography>
                        </Box>
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "row",
                            alignItems: "center",
                            gap: 1,
                            width: "100%",
                          }}
                        >
                          <Box sx={{ flexGrow: 1 }} />
                          <Button
                            disableElevation
                            onClick={handleIntroductionCancel}
                            variant="text"
                            color="primary"
                            size="small"
                            sx={{ minWidth: "75px" }}
                          >
                            {t("common.cancel")}
                          </Button>
                          <Button
                            disableElevation
                            type={"submit"}
                            variant="outlined"
                            color="primary"
                            startIcon={<SaveIcon />}
                            size="small"
                            disabled={isLoadingIntroduction}
                          >
                            {t("common.save")}
                          </Button>
                        </Box>
                      </Box>
                    </form>
                  ) : (
                    <Box flexDirection={"row"} sx={{ display: "flex", alignItems: "flex-end", width: "100%" }}>
                      <TextField
                        variant={"standard"}
                        disabled
                        multiline
                        rows={5}
                        value={introduction}
                        sx={{ width: "100%", marginLeft: 1 }}
                      />
                      <IconButton onClick={handleEditIntroduction} size="small">
                        <EditIcon fontSize="inherit" />
                      </IconButton>
                    </Box>
                  )}
                </Grid>
              </Grid>
            </CustomSection>

            {/* ログアウト */}
            <CustomSection>
              <Grid container sx={{ justifyContent: "space-between", alignItems: "center" }}>
                <Grid
                  size={{
                    xs: 12,
                    md: 6,
                  }}
                >
                  <Typography variant="subtitle1">{t("top.logout")}</Typography>
                </Grid>
                <Grid
                  sx={{ display: "flex", justifyContent: "flex-end" }}
                  size={{
                    xs: 12,
                    md: 6,
                  }}
                >
                  <Button
                    disableElevation
                    onClick={handleLogout}
                    variant="text"
                    sx={{ width: 180 }}
                    color="error"
                    startIcon={<LogoutIcon />}
                  >
                    {t("top.logout")}
                  </Button>
                </Grid>
              </Grid>
            </CustomSection>

            {/* アカウント削除 */}
            <CustomSection>
              <Grid container sx={{ justifyContent: "space-between", alignItems: "center" }}>
                <Grid
                  size={{
                    xs: 12,
                    md: 6,
                  }}
                >
                  <Typography variant="subtitle1">{t("top.delete")}</Typography>
                </Grid>
                <Grid
                  sx={{ display: "flex", justifyContent: "flex-end" }}
                  size={{
                    xs: 12,
                    md: 6,
                  }}
                >
                  <Button
                    disableElevation
                    onClick={() => setOpenDeleteDialog(true)}
                    variant="contained"
                    sx={{ width: 180 }}
                    color="error"
                    startIcon={<DeleteIcon />}
                  >
                    {t("top.delete")}
                  </Button>
                </Grid>
              </Grid>
            </CustomSection>
          </Paper>
        </Fade>
      </Box>
      {/* アカウント削除ダイアログ */}
      <Dialog open={openDeleteDialog} onClose={() => setOpenDeleteDialog(false)}>
        <Box sx={{ p: 2 }}>
          <DialogTitle>{t("preferences.account.deleteDialog.title")}</DialogTitle>
          <DialogContent>
            <Typography gutterBottom>{t("preferences.account.deleteDialog.description")}</Typography>
            {user?.user_type === "personal" ? (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {t("preferences.account.deleteDialog.personalUser")}
              </Typography>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {t("preferences.account.deleteDialog.enterpriseUser")}
              </Typography>
            )}
          </DialogContent>
          <DialogActions>
            <Button
              disableElevation
              onClick={() => setOpenDeleteDialog(false)}
              variant="text"
              color="primary"
              startIcon={<CancelIcon />}
            >
              {t("common.cancel")}
            </Button>
            <Button
              disableElevation
              onClick={handleDelete}
              variant="contained"
              color="error"
              startIcon={<DeleteIcon />}
            >
              {t("preferences.account.deleteDialog.confirm")}
            </Button>
          </DialogActions>
        </Box>
      </Dialog>
      {/* 組織選択ダイアログ */}
      {/* 組織選択ダイアログ */}
      <Dialog open={organizationDialogOpen} onClose={() => setOrganizationDialogOpen(false)} fullWidth maxWidth="sm">
        <Box sx={{ p: 2 }}>
          <DialogTitle>{t("preferences.enterprise.selectOrganization")}</DialogTitle>
          <DialogContent>
            {loadingOrganizations ? (
              <Typography>{t("preferences.enterprise.loading")}</Typography>
            ) : availableOrganizations.length === 0 ? (
              <Typography>{t("preferences.enterprise.noOrganizations")}</Typography>
            ) : (
              <Box sx={{ mt: 2 }}>
                {availableOrganizations.map((org) => (
                  <Box
                    key={org.id}
                    sx={{
                      p: 2,
                      mb: 1,
                      border: "1px solid",
                      borderColor: selectedOrganization === org.id ? "primary.main" : "grey.300",
                      borderRadius: 1,
                      cursor: "pointer",
                      "&:hover": {
                        bgcolor: "action.hover",
                      },
                    }}
                    onClick={() => setSelectedOrganization(org.id)}
                  >
                    <Typography variant="subtitle1">{org.name}</Typography>
                    <Typography variant="body2">{t(`preferences.enterprise.role.${org.role}`)}</Typography>
                  </Box>
                ))}
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button disableElevation onClick={() => setOrganizationDialogOpen(false)} variant="text" color="primary">
              {t("common.cancel")}
            </Button>
            <Button
              disableElevation
              onClick={handleOrganizationDialogConfirm}
              variant="contained"
              color="primary"
              disabled={!selectedOrganization || loadingOrganizations}
            >
              {t("preferences.enterprise.switchMode")}
            </Button>
          </DialogActions>
        </Box>
      </Dialog>
    </>
  );
};

export default Account;
